// src/pages/ActualitePage.tsx
import { useEffect, useState, useCallback, useMemo } from 'react';
import { Calendar, Clock, ArrowRight, Search, X, Loader2, TrendingUp, Tag, Share2, ChevronLeft, ChevronRight, Eye, Facebook, Twitter, Linkedin } from 'lucide-react';
import { Link } from '../components/Router';
import { LayoutWithBanners } from '../components/LayoutWithBanners';
import { trackPageView } from '../utils/analytics';
import { supabase } from '../lib/supabase';
import { slugify } from '../utils/slugify';

interface ActualiteItem {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  author: string;
  publish_date: string;
  read_time: string;
  category: string;
  image_url: string;
  featured: boolean;
  views?: number;
  images?: ArticleImage[];
  slug?: string;
}

interface ArticleImage {
  id: string;
  article_id: string;
  image_url: string;
  alt_text?: string;
  display_order: number;
}

const CATEGORIES = ['Toutes', 'Actualité', 'Essai', 'Fiabilité', 'Passion', 'Réglementation', 'Technique'];
const CATEGORY_COLORS: Record<string, string> = {
  'Actualité': 'bg-blue-50 text-blue-700',
  'Essai': 'bg-green-50 text-green-700',
  'Fiabilité': 'bg-purple-50 text-purple-700',
  'Passion': 'bg-pink-50 text-pink-700',
  'Réglementation': 'bg-amber-50 text-amber-700',
  'Technique': 'bg-red-50 text-red-700'
};

const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

// --- UPDATED SHARE BUTTON ---
const ShareButton = ({ articleUrl, title }: { articleUrl: string, title?: string }) => {
  const [isShareMenuOpen, setIsShareMenuOpen] = useState(false);
  const encodedUrl = encodeURIComponent(articleUrl);
  const encodedTitle = encodeURIComponent(title || '');

  const openShareWindow = (e: React.MouseEvent, url: string) => {
    e.preventDefault();
    e.stopPropagation();
    window.open(url, '_blank', 'width=600,height=400,noopener,noreferrer');
    setIsShareMenuOpen(false);
  };

  return (
    <div className="relative z-50">
      <button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsShareMenuOpen(!isShareMenuOpen);
        }}
        className="p-2 bg-gray-100 rounded-full text-gray-600 hover:text-gray-900 hover:bg-gray-200 transition-colors"
        aria-label="Share article"
      >
        <Share2 className="w-4 h-4" />
      </button>

      {isShareMenuOpen && (
        <div className="absolute top-full right-0 mt-2 p-2 bg-white/95 backdrop-blur-xl rounded-2xl border border-gray-200 shadow-xl flex gap-2 animate-in fade-in zoom-in duration-200 w-max">
          <button
            onClick={(e) => openShareWindow(e, `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`)}
            className="p-2 text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
            title="Partager sur Facebook"
          >
            <Facebook className="w-5 h-5" />
          </button>
          <button
            onClick={(e) => openShareWindow(e, `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`)}
            className="p-2 text-black hover:bg-gray-100 rounded-full transition-colors"
            title="Partager sur X (Twitter)"
          >
            <Twitter className="w-5 h-5" />
          </button>
          <button
            onClick={(e) => openShareWindow(e, `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`)}
            className="p-2 text-blue-700 hover:bg-blue-50 rounded-full transition-colors"
            title="Partager sur LinkedIn"
          >
            <Linkedin className="w-5 h-5" />
          </button>
        </div>
      )}
    </div>
  );
};

const ArticleSkeleton = () => (
  <div className="bg-white/80 backdrop-blur-xl rounded-3xl overflow-hidden border border-gray-200 animate-pulse">
    <div className="h-48 bg-gray-100"></div>
    <div className="p-6">
      <div className="h-5 bg-gray-100 rounded-2xl mb-3"></div>
      <div className="h-4 bg-gray-100 rounded-2xl mb-2"></div>
      <div className="h-4 bg-gray-100 rounded-2xl w-5/6 mb-4"></div>
      <div className="flex justify-between items-center">
        <div className="h-4 bg-gray-100 rounded-2xl w-1/4"></div>
        <div className="h-9 bg-gray-100 rounded-full w-24"></div>
      </div>
    </div>
  </div>
);

export function ActualitePage() {
  const [newsItems, setNewsItems] = useState<ActualiteItem[]>([]);
  const [filteredItems, setFilteredItems] = useState<ActualiteItem[]>([]);
  const [selectedCategory, setSelectedCategory] = useState('Toutes');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [totalCount, setTotalCount] = useState(0);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const itemsPerPage = 6;
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .prose .custom-table-container { margin: 0.75em 0; overflow-x: auto; }
      .prose .custom-table { width: 100%; border-collapse: collapse; }
      .prose .custom-table th, .prose .custom-table td { border: 1px solid #e5e7eb; padding: 6px 10px; text-align: left; font-size: 0.875rem; }
      .prose .custom-table th { background-color: #f9fafb; font-weight: 600; color: #374151; }
      .prose .custom-table tr:nth-child(even) { background-color: #f9fafb; }
      @media (max-width: 640px) {
        .prose .custom-table { font-size: 0.8125rem; }
        .prose .custom-table th, .prose .custom-table td { padding: 4px 6px; }
      }
    `;
    document.head.appendChild(style);
    return () => { if (document.head.contains(style)) document.head.removeChild(style); };
  }, []);

  const fetchPageData = useCallback(async (page: number, category: string, search: string) => {
    try {
      if (isInitialLoad || page !== currentPage) setIsLoading(true);
      setError(null);

      let query = supabase.from('actualite').select('id, title, excerpt, content, publish_date, read_time, category, image_url, featured, views, slug', { count: 'exact' });

      if (category !== 'Toutes') query = query.eq('category', category);
      if (search) query = query.ilike('title', `%${search}%`).or(`excerpt.ilike.%${search}%`);

      const from = (page - 1) * itemsPerPage;
      const to = from + itemsPerPage - 1;
      const { data: articles, error: articlesError, count } = await query.order('publish_date', { ascending: false }).range(from, to);

      if (articlesError) throw articlesError;

      if (!articles || articles.length === 0) {
        setNewsItems([]);
        setFilteredItems([]);
        setTotalCount(count || 0);
        return;
      }

      const imagePromises = articles.map(article => supabase.from('article_images').select('id, image_url, alt_text').eq('article_id', article.id).order('display_order', { ascending: true }).limit(1));
      const imageResults = await Promise.all(imagePromises);
      const allImages = imageResults.flatMap(result => result.data || []);

      const articlesWithImages: ActualiteItem[] = articles.map(article => ({
        ...article,
        images: allImages.filter(img => img.article_id === article.id),
      }));

      setNewsItems(articlesWithImages);
      setFilteredItems(articlesWithImages);
      setTotalCount(count || 0);

    } catch (err: any) {
      console.error('Error fetching news items:', err);
      setError('Impossible de charger les actualités.');
      setNewsItems([]);
      setFilteredItems([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
      if (isInitialLoad) setIsInitialLoad(false);
    }
  }, [currentPage, isInitialLoad]);

  useEffect(() => {
    trackPageView('/actualite');
    fetchPageData(currentPage, selectedCategory, debouncedSearchTerm);
  }, [currentPage, selectedCategory, debouncedSearchTerm, fetchPageData]);

  const totalPages = Math.ceil(totalCount / itemsPerPage);
  const featuredNews = newsItems.find(item => item.featured);
  const truncateText = (text: string, maxLength: number) => text.length <= maxLength ? text : text.slice(0, maxLength) + '...';

  const handlePageChange = (pageNumber: number) => { setCurrentPage(pageNumber); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const handleFilterChange = (category: string) => { setSelectedCategory(category); setCurrentPage(1); };
  const handleSearchChange = (term: string) => { setSearchTerm(term); setCurrentPage(1); };

  const articlesGrid = useMemo(() => {
    if (viewMode === 'grid') {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredItems.map((item) => {
            const articleUrl = `${window.location.origin}/actualite/${item.slug || item.id}`;
            return (
              // REMOVED overflow-hidden from article to allow popup to show
              <article key={item.id} className="bg-white/80 backdrop-blur-xl rounded-3xl hover:bg-white transition-all duration-300 border border-gray-200">
                <div className="relative h-48 overflow-hidden rounded-t-3xl">
                  <Link to={`/actualite/${item.slug || item.id}`} className="block h-full">
                    <img src={item.image_url} alt={item.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" onError={(e) => { e.currentTarget.src = '/placeholder-image.jpg'; }} />
                  </Link>
                  <div className="absolute top-3 left-3">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${CATEGORY_COLORS[item.category] || 'bg-gray-50 text-gray-800'}`}>{item.category}</span>
                  </div>
                  <div className="absolute bottom-3 right-3 bg-black/40 backdrop-blur-md text-white text-xs px-3 py-1 rounded-full flex items-center">
                    <Clock className="h-3 w-3 mr-1" />{item.read_time}
                  </div>
                </div>
                <div className="p-6">
                  <Link to={`/actualite/${item.slug || item.id}`} className="block">
                    <h3 className="text-base font-semibold text-gray-900 mb-2 line-clamp-2 hover:text-[#D5462E] transition-colors">{item.title}</h3>
                  </Link>
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2 leading-relaxed">{item.excerpt}</p>
                  <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                    <span className="flex items-center"><Calendar className="h-4 w-4 mr-1" />{new Date(item.publish_date).toLocaleDateString('fr-FR')}</span>
                    {item.views && <span className="flex items-center"><Eye className="h-4 w-4 mr-1" />{item.views}</span>}
                  </div>
                  <div className="flex items-center justify-between">
                    <Link to={`/actualite/${item.slug || item.id}`} className="bg-[#D5462E] text-white px-5 py-2 rounded-full text-sm font-medium hover:bg-[#B73C28] transition-all flex items-center">Lire <ArrowRight className="h-4 w-4 ml-1" /></Link>
                    <ShareButton articleUrl={articleUrl} title={item.title} />
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      );
    } else {
      return (
        <div className="space-y-4">
          {filteredItems.map((item) => {
            const articleUrl = `${window.location.origin}/actualite/${item.slug || item.id}`;
            return (
              <article key={item.id} className="bg-white/80 backdrop-blur-xl rounded-3xl hover:bg-white transition-all duration-300 border border-gray-200">
                <div className="md:flex">
                  <div className="md:w-1/3 relative h-48 md:h-auto overflow-hidden rounded-t-3xl md:rounded-l-3xl md:rounded-tr-none">
                    <Link to={`/actualite/${item.slug || item.id}`} className="block h-full">
                      <img src={item.image_url} alt={item.title} className="h-48 md:h-full w-full object-cover hover:scale-105 transition-transform duration-500" onError={(e) => { e.currentTarget.src = '/placeholder-image.jpg'; }} />
                    </Link>
                    <div className="absolute top-3 left-3">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${CATEGORY_COLORS[item.category] || 'bg-gray-50 text-gray-800'}`}>{item.category}</span>
                    </div>
                    <div className="absolute top-3 right-3">
                      <ShareButton articleUrl={articleUrl} title={item.title} />
                    </div>
                  </div>
                  <div className="p-6 md:w-2/3 flex flex-col justify-between">
                    <div>
                      <Link to={`/actualite/${item.slug || item.id}`} className="block">
                        <h3 className="text-xl font-semibold text-gray-900 mb-2 hover:text-[#D5462E] transition-colors">{item.title}</h3>
                      </Link>
                      <p className="text-gray-600 mb-3 line-clamp-2 leading-relaxed">{item.excerpt}</p>
                      <div className="flex items-center text-sm text-gray-500 space-x-4">
                        <span className="flex items-center"><Calendar className="h-4 w-4 mr-1" />{new Date(item.publish_date).toLocaleDateString('fr-FR')}</span>
                        <span className="flex items-center"><Clock className="h-4 w-4 mr-1" />{item.read_time}</span>
                        {item.views && <span className="flex items-center"><Eye className="h-4 w-4 mr-1" />{item.views}</span>}
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-4">
                      <Link to={`/actualite/${item.slug || item.id}`} className="bg-[#D5462E] text-white px-5 py-2 rounded-full font-medium hover:bg-[#B73C28] transition-all flex items-center">Lire <ArrowRight className="h-4 w-4 ml-1.5" /></Link>
                    </div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      );
    }
  }, [filteredItems, viewMode]);

  return (
    <div className="min-h-screen bg-white">
      <LayoutWithBanners pageLocation="actualite" showTopBanner={true} showBottomBanner={true} showLeftSidebar={false} showRightSidebar={false}>
        {featuredNews && !isLoading && !error && (
          <div className="w-full px-4 sm:px-6 lg:px-8 mt-8">
            {/* Removed overflow-hidden from parent card, applied to children instead */}
            <div className="bg-white/80 backdrop-blur-xl rounded-3xl border border-gray-200">
              <div className="md:flex">
                <div className="md:w-2/5 relative h-56 md:h-auto overflow-hidden rounded-t-3xl md:rounded-l-3xl md:rounded-tr-none">
                  <Link to={`/actualite/${featuredNews.slug || featuredNews.id}`} className="block h-full">
                    <img src={featuredNews.image_url} alt={featuredNews.title} className="h-56 md:h-full w-full object-cover hover:scale-105 transition-transform duration-500" onError={(e) => { e.currentTarget.src = '/placeholder-image.jpg'; }} />
                  </Link>
                  <div className="absolute top-4 left-4 bg-[#D5462E] text-white px-4 py-1.5 rounded-full text-sm font-semibold flex items-center"><TrendingUp className="h-4 w-4 mr-1" />À la une</div>
                </div>
                <div className="p-8 md:w-3/5 flex flex-col justify-center">
                  <div className="flex items-center mb-3 space-x-3">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${CATEGORY_COLORS[featuredNews.category] || 'bg-gray-50 text-gray-800'}`}><Tag className="h-3.5 w-3.5 mr-1" />{featuredNews.category}</span>
                    <span className="text-sm text-gray-500 flex items-center"><Clock className="h-4 w-4 mr-1" />{featuredNews.read_time}</span>
                    {featuredNews.views && <span className="text-sm text-gray-500 flex items-center"><Eye className="h-4 w-4 mr-1" />{featuredNews.views}</span>}
                  </div>
                  <Link to={`/actualite/${featuredNews.slug || featuredNews.id}`} className="block">
                    <h2 className="text-2xl font-semibold text-gray-900 mb-3 hover:text-[#D5462E] transition-colors leading-tight">{featuredNews.title}</h2>
                  </Link>
                  <p className="text-gray-600 mb-5 leading-relaxed">{truncateText(featuredNews.excerpt, 180)}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center text-sm text-gray-500"><span className="flex items-center"><Calendar className="h-4 w-4 mr-1" />{new Date(featuredNews.publish_date).toLocaleDateString('fr-FR')}</span></div>
                    <div className="flex items-center space-x-3">
                      <ShareButton articleUrl={`${window.location.origin}/actualite/${featuredNews.slug || featuredNews.id}`} title={featuredNews.title} />
                      <Link to={`/actualite/${featuredNews.slug || featuredNews.id}`} className="bg-[#D5462E] text-white px-5 py-2.5 rounded-full font-medium hover:bg-[#B73C28] transition-all flex items-center">Lire <ArrowRight className="h-4 w-4 ml-1.5" /></Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white/80 backdrop-blur-xl rounded-3xl border border-gray-200 p-8 mb-6">
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <input type="text" placeholder="Rechercher un article..." value={searchTerm} onChange={(e) => handleSearchChange(e.target.value)} className="w-full pl-12 pr-4 py-3 bg-gray-100 text-gray-900 rounded-2xl focus:outline-none focus:bg-white focus:ring-2 focus:ring-[#D5462E]/10 transition-all" />
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {CATEGORIES.map(category => (
                  <button key={category} onClick={() => handleFilterChange(category)} className={`px-4 py-2.5 rounded-full font-medium transition-all text-sm ${selectedCategory === category ? 'bg-[#D5462E] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>{category}</button>
                ))}
              </div>
            </div>
            {(searchTerm || selectedCategory !== 'Toutes') && (
              <div className="mt-6 pt-6 border-t border-gray-200 flex items-center justify-between">
                <p className="text-gray-600 font-medium text-sm"><span className="font-semibold text-gray-900">{totalCount}</span> article{totalCount > 1 ? 's' : ''} trouvé{totalCount > 1 ? 's' : ''}</p>
                <button onClick={() => { handleSearchChange(''); handleFilterChange('Toutes'); }} className="text-[#D5462E] hover:text-[#B73C28] font-medium flex items-center text-sm"><X className="h-4 w-4 mr-1" />Effacer</button>
              </div>
            )}
          </div>

          {isLoading && isInitialLoad ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">{[...Array(itemsPerPage)].map((_, index) => <ArticleSkeleton key={index} />)}</div>
          ) : error ? (
            <div className="flex justify-center items-center py-16">
              <div className="text-center bg-red-50 backdrop-blur-xl p-8 rounded-3xl border border-red-200 max-w-md">
                <X className="h-12 w-12 text-red-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-red-900 mb-2">Erreur</h3>
                <p className="text-red-700 mb-5">{error}</p>
                <button onClick={() => fetchPageData(currentPage, selectedCategory, debouncedSearchTerm)} className="bg-red-600 text-white px-5 py-2.5 rounded-full hover:bg-red-700 font-medium">Réessayer</button>
              </div>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="flex justify-center items-center py-16">
              <div className="text-center max-w-md">
                <Search className="h-14 w-14 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Aucun article trouvé</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">{searchTerm || selectedCategory !== 'Toutes' ? 'Aucun article ne correspond à vos critères.' : 'Aucune actualité disponible.'}</p>
                {(searchTerm || selectedCategory !== 'Toutes') && (
                  <button onClick={() => { handleSearchChange(''); handleFilterChange('Toutes'); }} className="bg-[#D5462E] text-white px-5 py-2.5 rounded-full hover:bg-[#B73C28] font-medium">Voir toutes les actualités</button>
                )}
              </div>
            </div>
          ) : (
            <>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-gray-900">{selectedCategory === 'Toutes' ? 'Toutes les actualités' : selectedCategory}</h2>
                <div className="flex items-center space-x-1 bg-gray-100 rounded-2xl p-1">
                  <button onClick={() => setViewMode('grid')} className={`p-2 rounded-xl transition-all ${viewMode === 'grid' ? 'bg-white' : ''}`}>
                    <div className="grid grid-cols-2 gap-0.5 w-4 h-4">
                      <div className={`rounded-sm ${viewMode === 'grid' ? 'bg-gray-900' : 'bg-gray-600'}`}></div>
                      <div className={`rounded-sm ${viewMode === 'grid' ? 'bg-gray-900' : 'bg-gray-600'}`}></div>
                      <div className={`rounded-sm ${viewMode === 'grid' ? 'bg-gray-900' : 'bg-gray-600'}`}></div>
                      <div className={`rounded-sm ${viewMode === 'grid' ? 'bg-gray-900' : 'bg-gray-600'}`}></div>
                    </div>
                  </button>
                  <button onClick={() => setViewMode('list')} className={`p-2 rounded-xl transition-all ${viewMode === 'list' ? 'bg-white' : ''}`}>
                    <div className="space-y-1 w-4 h-4">
                      <div className={`rounded-sm h-0.5 ${viewMode === 'list' ? 'bg-gray-900' : 'bg-gray-600'}`}></div>
                      <div className={`rounded-sm h-0.5 ${viewMode === 'list' ? 'bg-gray-900' : 'bg-gray-600'}`}></div>
                      <div className={`rounded-sm h-0.5 ${viewMode === 'list' ? 'bg-gray-900' : 'bg-gray-600'}`}></div>
                    </div>
                  </button>
                </div>
              </div>
              {articlesGrid}
              {totalPages > 1 && (
                <div className="flex justify-center mt-8">
                  <nav className="flex items-center space-x-2 bg-white/80 backdrop-blur-xl rounded-full p-2 border border-gray-200">
                    <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="p-2 rounded-full hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-all"><ChevronLeft className="h-5 w-5" /></button>
                    <div className="flex space-x-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                        <button key={page} onClick={() => handlePageChange(page)} className={`px-4 py-2 rounded-full font-medium transition-all text-sm ${currentPage === page ? 'bg-[#D5462E] text-white' : 'text-gray-700 hover:bg-gray-100'}`}>{page}</button>
                      ))}
                    </div>
                    <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="p-2 rounded-full hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-all"><ChevronRight className="h-5 w-5" /></button>
                  </nav>
                </div>
              )}
            </>
          )}
        </div>
      </LayoutWithBanners>
    </div>
  );
}